/**
 * ChatPanel — global chat overlay.
 *
 * Shows the last ~15 messages in a corner. Press T to focus the input.
 * Enter sends, Esc closes.
 */

import { html, render } from 'lit-html';
import { useUI } from '@/state/store';
import { ChatChannels } from '@shared/protocol';

export interface ChatCallbacks {
  onSend: (text: string) => void;
  onClose: () => void;
}

export class ChatPanel {
  private container: HTMLDivElement;
  private open = false;
  private draft = '';

  constructor(private cb: ChatCallbacks) {
    const root = document.getElementById('ui-root')!;
    this.container = document.createElement('div');
    this.container.className = 'chat';
    root.appendChild(this.container);
    useUI.subscribe(() => this.render());
    this.render();
  }

  setOpen(open: boolean): void {
    if (this.open === open) return;
    this.open = open;
    this.render();
    if (open) {
      requestAnimationFrame(() => {
        const el = this.container.querySelector('.chat-input') as HTMLInputElement | null;
        el?.focus();
      });
    } else {
      this.draft = '';
    }
  }

  isOpen(): boolean {
    return this.open;
  }

  private send(): void {
    const t = this.draft.trim();
    if (t) this.cb.onSend(t);
    this.draft = '';
    this.setOpen(false);
    this.cb.onClose();
  }

  private render(): void {
    const messages = useUI.getState().chat.slice(-15);
    const tpl = html`
      <div class="chat-list ${this.open ? 'open' : ''}">
        ${messages.map(
          (m) => html`
            <div class="chat-msg ${m.channel === ChatChannels.System ? 'sys' : ''}">
              <span class="chat-name">${m.fromName}</span>
              <span class="chat-text">${m.text}</span>
            </div>
          `
        )}
      </div>
      ${this.open
        ? html`
            <div class="chat-input-row">
              <span class="chat-prompt">say:</span>
              <input
                class="chat-input"
                type="text"
                maxlength="200"
                .value=${this.draft}
                @input=${(e: Event) => {
                  this.draft = (e.target as HTMLInputElement).value;
                }}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') this.send();
                  if (e.key === 'Escape') {
                    this.setOpen(false);
                    this.cb.onClose();
                  }
                }}
              />
            </div>
          `
        : ''}
    `;
    render(tpl, this.container);
  }
}
